import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AdminPageHeader, RowActions } from "@/components/admin/crud-helpers";
import {
  upsertMembershipPlan, deleteMembershipPlan, assignMembership, cancelMembership,
} from "@/lib/memberships.functions";
import { Plus, Star, Users, CreditCard, Search, Check, Ban, Clock } from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/plans")({
  component: PlansPage,
});

type Plan = {
  id: string; name: string; slug: string; description: string | null;
  tier: number; price_monthly_cents: number; price_yearly_cents: number;
  currency: string; trial_days: number; features: string[];
  feature_flags: Record<string, boolean | number | string>;
  highlighted: boolean; active: boolean; sort_order: number;
};

type Membership = {
  id: string; user_id: string; plan_id: string; status: string;
  billing_interval: string; trial_ends_at: string | null;
  current_period_end: string | null; created_at: string;
};

const emptyPlan: Plan = {
  id: "", name: "", slug: "", description: "", tier: 1,
  price_monthly_cents: 0, price_yearly_cents: 0, currency: "USD",
  trial_days: 0, features: [], feature_flags: {},
  highlighted: false, active: true, sort_order: 0,
};

function money(cents: number, cur = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(cents / 100);
}

function PlansPage() {
  return (
    <Tabs defaultValue="plans" className="space-y-6">
      <AdminPageHeader title="Membership plans" description="Tiered subscription plans, trials, and feature flags." />
      <PillTabsList search>
        <PillTabsTrigger value="plans"><CreditCard className="mr-2 h-4 w-4" />Plans</PillTabsTrigger>
        <PillTabsTrigger value="members"><Users className="mr-2 h-4 w-4" />Members</PillTabsTrigger>
      </PillTabsList>
      <TabsContent value="plans"><PlansTab /></TabsContent>
      <TabsContent value="members"><MembersTab /></TabsContent>
    </Tabs>
  );
}

function PlansTab() {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertMembershipPlan);
  const del = useServerFn(deleteMembershipPlan);
  const [editing, setEditing] = useState<Plan | null>(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["membership_plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("membership_plans").select("*").order("sort_order").order("tier");
      if (error) throw error;
      return data as Plan[];
    },
  });

  const saveMut = useMutation({
    mutationFn: (p: Plan) => upsert({ data: { ...p, id: p.id || undefined, description: p.description || null } }),
    onSuccess: () => { toast.success("Plan saved"); qc.invalidateQueries({ queryKey: ["membership_plans"] }); setEditing(null); },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Plan deleted"); qc.invalidateQueries({ queryKey: ["membership_plans"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const [query, setQuery] = useState("");
  const stats = useMemo(() => {
    const active = plans.filter((p) => p.active).length;
    const highlighted = plans.filter((p) => p.highlighted).length;
    const trials = plans.filter((p) => p.trial_days > 0).length;
    return { total: plans.length, active, highlighted, trials };
  }, [plans]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      p.slug.toLowerCase().includes(q) ||
      (p.description ?? "").toLowerCase().includes(q),
    );
  }, [plans, query]);

  return (
    <div className="space-y-4">
      <StatsStrip
        tiles={[
          { label: "Plans", value: stats.total, icon: CreditCard },
          { label: "Active", value: stats.active, icon: Check, tone: "success" },
          { label: "Highlighted", value: stats.highlighted, icon: Star, tone: "info" },
          { label: "With trial", value: stats.trials, icon: Clock, tone: "warning" },
        ]}
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search plans…" className="pl-8" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <Button className="ml-auto" onClick={() => setEditing({ ...emptyPlan })}><Plus className="mr-2 h-4 w-4" />New plan</Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map(p => (
            <Card key={p.id} className={`transition-shadow hover:shadow-md ${p.highlighted ? "border-primary ring-1 ring-primary/20" : ""}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {p.name}
                      {p.highlighted && <Star className="h-4 w-4 fill-primary text-primary" />}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Tier {p.tier} · {p.slug}</p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {!p.active && <Badge variant="secondary">Inactive</Badge>}
                    {p.trial_days > 0 && <Badge variant="outline">{p.trial_days}d trial</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md bg-muted/40 p-2 text-sm">
                  <div className="text-lg font-semibold tabular-nums">{money(p.price_monthly_cents, p.currency)} <span className="text-xs font-normal text-muted-foreground">/ mo</span></div>
                  <div className="text-xs text-muted-foreground tabular-nums">{money(p.price_yearly_cents, p.currency)} / yr</div>
                </div>
                {p.features.length > 0 && (
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
                    {p.features.slice(0, 6).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                )}
                <RowActions
                  onEdit={() => setEditing(p)}
                  onDelete={() => { if (confirm(`Delete plan "${p.name}"?`)) delMut.mutate(p.id); }}
                />
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card className="sm:col-span-2 xl:col-span-3">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {plans.length === 0 ? "No plans yet. Create your first plan." : "No plans match your search."}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <PlanDialog
        plan={editing}
        onClose={() => setEditing(null)}
        onSave={(p) => saveMut.mutate(p)}
        saving={saveMut.isPending}
      />
    </div>
  );
}

function PlanDialog({ plan, onClose, onSave, saving }: {
  plan: Plan | null; onClose: () => void; onSave: (p: Plan) => void; saving: boolean;
}) {
  const [draft, setDraft] = useState<Plan | null>(plan);
  const [featuresText, setFeaturesText] = useState("");
  const [flagsText, setFlagsText] = useState("{}");

  useMemo(() => {
    setDraft(plan);
    setFeaturesText((plan?.features ?? []).join("\n"));
    setFlagsText(JSON.stringify(plan?.feature_flags ?? {}, null, 2));
  }, [plan]);

  if (!draft) return null;

  const set = <K extends keyof Plan>(k: K, v: Plan[K]) => setDraft({ ...draft, [k]: v });

  const submit = () => {
    let flags: Plan["feature_flags"] = {};
    try { flags = JSON.parse(flagsText || "{}"); }
    catch { toast.error("Feature flags must be valid JSON"); return; }
    const features = featuresText.split("\n").map(s => s.trim()).filter(Boolean);
    onSave({ ...draft, features, feature_flags: flags });
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{draft.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2 md:grid-cols-2">
          <div className="space-y-2"><Label>Name</Label><Input value={draft.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="space-y-2"><Label>Slug</Label><Input value={draft.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} /></div>
          <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea value={draft.description ?? ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="space-y-2"><Label>Tier</Label><Input type="number" value={draft.tier} onChange={(e) => set("tier", parseInt(e.target.value || "0"))} /></div>
          <div className="space-y-2"><Label>Sort order</Label><Input type="number" value={draft.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "0"))} /></div>
          <div className="space-y-2"><Label>Monthly price</Label><Input type="number" step="0.01" value={draft.price_monthly_cents / 100} onChange={(e) => set("price_monthly_cents", Math.round(parseFloat(e.target.value || "0") * 100))} /></div>
          <div className="space-y-2"><Label>Yearly price</Label><Input type="number" step="0.01" value={draft.price_yearly_cents / 100} onChange={(e) => set("price_yearly_cents", Math.round(parseFloat(e.target.value || "0") * 100))} /></div>
          <div className="space-y-2"><Label>Currency</Label><Input value={draft.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} /></div>
          <div className="space-y-2"><Label>Trial days</Label><Input type="number" value={draft.trial_days} onChange={(e) => set("trial_days", parseInt(e.target.value || "0"))} /></div>
          <div className="space-y-2 md:col-span-2"><Label>Features (one per line)</Label><Textarea rows={5} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"Unlimited projects\nPriority support"} /></div>
          <div className="space-y-2 md:col-span-2">
            <Label>Feature flags (JSON)</Label>
            <Textarea rows={5} value={flagsText} onChange={(e) => setFlagsText(e.target.value)} className="font-mono text-xs" placeholder='{ "ai_credits": 1000, "white_label": true }' />
          </div>
          <div className="flex items-center justify-between rounded border p-3"><Label>Highlighted</Label><Switch checked={draft.highlighted} onCheckedChange={(v) => set("highlighted", v)} /></div>
          <div className="flex items-center justify-between rounded border p-3"><Label>Active</Label><Switch checked={draft.active} onCheckedChange={(v) => set("active", v)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save plan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersTab() {
  const qc = useQueryClient();
  const assign = useServerFn(assignMembership);
  const cancel = useServerFn(cancelMembership);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", plan_id: "", billing_interval: "monthly" as "monthly" | "yearly", start_trial: false, notes: "" });

  const { data: rows = [] } = useQuery({
    queryKey: ["user_memberships"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_memberships").select("*").order("created_at", { ascending: false }).limit(500);
      if (error) throw error;
      return data as Membership[];
    },
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["membership_plans", "all"],
    queryFn: async () => {
      const { data } = await supabase.from("membership_plans").select("id,name,slug").order("tier");
      return (data ?? []) as { id: string; name: string; slug: string }[];
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["profiles", "for-memberships", rows.map(r => r.user_id).join(",")],
    enabled: rows.length > 0,
    queryFn: async () => {
      const ids = Array.from(new Set(rows.map(r => r.user_id)));
      const { data } = await supabase.from("profiles").select("id,display_name").in("id", ids);
      return (data ?? []) as { id: string; display_name: string | null }[];
    },
  });

  const planMap = useMemo(() => Object.fromEntries(plans.map(p => [p.id, p.name])), [plans]);
  const userMap = useMemo(() => Object.fromEntries(profiles.map(p => [p.id, p.display_name ?? p.id.slice(0, 8)])), [profiles]);

  const assignMut = useMutation({
    mutationFn: () => assign({ data: { ...form, notes: form.notes || undefined } }),
    onSuccess: () => { toast.success("Membership assigned"); qc.invalidateQueries({ queryKey: ["user_memberships"] }); setOpen(false); setForm({ user_id: "", plan_id: "", billing_interval: "monthly", start_trial: false, notes: "" }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancel({ data: { id } }),
    onSuccess: () => { toast.success("Cancelled"); qc.invalidateQueries({ queryKey: ["user_memberships"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const [mquery, setMquery] = useState("");
  const [mstatus, setMstatus] = useState<"all" | string>("all");
  const mstats = useMemo(() => {
    const active = rows.filter((r) => r.status === "active").length;
    const trialing = rows.filter((r) => r.status === "trialing").length;
    const cancelled = rows.filter((r) => r.status === "cancelled").length;
    return { total: rows.length, active, trialing, cancelled };
  }, [rows]);
  const fRows = useMemo(() => {
    const q = mquery.trim().toLowerCase();
    return rows.filter((r) => {
      if (mstatus !== "all" && r.status !== mstatus) return false;
      if (!q) return true;
      return (
        (userMap[r.user_id] ?? "").toLowerCase().includes(q) ||
        r.user_id.toLowerCase().includes(q) ||
        (planMap[r.plan_id] ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, mquery, mstatus, userMap, planMap]);

  const chips: { value: string; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "trialing", label: "Trialing" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div className="space-y-4">
      <StatsStrip
        tiles={[
          { label: "Members", value: mstats.total, icon: Users },
          { label: "Active", value: mstats.active, icon: Check, tone: "success" },
          { label: "Trialing", value: mstats.trialing, icon: Clock, tone: "warning" },
          { label: "Cancelled", value: mstats.cancelled, icon: Ban, tone: "danger" },
        ]}
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search member or plan…" className="pl-8" value={mquery} onChange={(e) => setMquery(e.target.value)} />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {chips.map((c) => (
            <Button key={c.value} size="sm" variant={mstatus === c.value ? "default" : "outline"} className="h-7 rounded-full px-3 text-xs" onClick={() => setMstatus(c.value)}>
              {c.label}
            </Button>
          ))}
        </div>
        <Button className="ml-auto" onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" />Assign</Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {fRows.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {rows.length === 0 ? "No memberships yet." : "No memberships match your filters."}
              </p>
            )}
            {fRows.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-4 p-4 hover:bg-muted/30">
                <div className="min-w-0">
                  <div className="font-medium truncate">{userMap[m.user_id] ?? m.user_id.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">
                    {planMap[m.plan_id] ?? "Unknown plan"} · {m.billing_interval}
                    {m.current_period_end && ` · ends ${new Date(m.current_period_end).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={m.status === "active" ? "default" : m.status === "trialing" ? "outline" : "secondary"}>{m.status}</Badge>
                  {m.status !== "cancelled" && (
                    <Button size="sm" variant="outline" onClick={() => cancelMut.mutate(m.id)}>Cancel</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign membership</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>User ID</Label><Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} placeholder="uuid" /></div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Billing</Label>
              <Select value={form.billing_interval} onValueChange={(v: "monthly" | "yearly") => setForm({ ...form, billing_interval: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded border p-3"><Label>Start with trial</Label><Switch checked={form.start_trial} onCheckedChange={(v) => setForm({ ...form, start_trial: v })} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => assignMut.mutate()} disabled={!form.user_id || !form.plan_id || assignMut.isPending}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
